import { useEffect, useRef, useState } from "react";
import type { LoadState } from "../../loadState";
import {
  loadMotionDetail,
  loadMotionOverview,
  type MotionOverview,
  type MotionSeries,
} from "./motionSeries";

/**
 * 닫힌 보기에서는 요청을 끊는다. 완료된 같은 세대의 데이터는 모드를 바꿔도 유지한다.
 */
export function useMotionOverview(
  url: string | null,
  enabled: boolean,
  retry: number,
): LoadState<MotionOverview | null> {
  const [loaded, setLoaded] = useState<{
    key: string;
    state: LoadState<MotionOverview | null>;
  } | null>(null);
  const cache = useRef<{ key: string; value: MotionOverview | null } | null>(null);
  const key = `${url ?? ""}|${retry}`;
  useEffect(() => {
    if (!enabled) return;
    if (!url) {
      setLoaded({ key, state: { status: "success", data: null } });
      return;
    }
    if (cache.current?.key === key) {
      setLoaded({ key, state: { status: "success", data: cache.current.value } });
      return;
    }
    const abort = new AbortController();
    setLoaded({ key, state: { status: "loading" } });
    void loadMotionOverview(url, abort.signal)
      .then((data) => {
        if (abort.signal.aborted) return;
        cache.current = { key, value: data };
        setLoaded({ key, state: { status: "success", data } });
      })
      .catch((error: unknown) => {
        if (!abort.signal.aborted)
          setLoaded({
            key,
            state: {
              status: "error",
              message:
                error instanceof Error ? error.message : "운동 데이터를 불러오지 못했습니다.",
            },
          });
      });
    return () => abort.abort();
  }, [url, enabled, key]);
  return loaded?.key === key ? loaded.state : { status: "loading" };
}

export function useMotionDetail(
  url: string | null,
  overview: MotionOverview | null,
  enabled: boolean,
  retry: number,
): LoadState<readonly MotionSeries[]> {
  const ranges = overview?.ranges ?? [];
  const rangeKey = ranges.map((r) => `${r.offset}:${r.length}`).join(",");
  const key = `${url ?? ""}|${overview?.generation ?? ""}|${rangeKey}|${retry}`;
  const [loaded, setLoaded] = useState<{
    key: string;
    state: LoadState<readonly MotionSeries[]>;
  } | null>(null);
  const cache = useRef<{ key: string; data: readonly MotionSeries[] } | null>(null);
  useEffect(() => {
    if (!enabled || !url || !overview) return;
    if (cache.current?.key === key) {
      setLoaded({ key, state: { status: "success", data: cache.current.data } });
      return;
    }
    const abort = new AbortController();
    setLoaded({ key, state: { status: "loading" } });
    void loadMotionDetail(url, overview, overview.ranges, abort.signal)
      .then((data) => {
        if (abort.signal.aborted) return;
        cache.current = { key, data };
        setLoaded({ key, state: { status: "success", data } });
      })
      .catch((error: unknown) => {
        if (!abort.signal.aborted)
          setLoaded({
            key,
            state: {
              status: "error",
              message:
                error instanceof Error ? error.message : "상세 데이터를 불러오지 못했습니다.",
            },
          });
      });
    return () => abort.abort();
    // 전체 상세는 한 번만 읽는다. 줌과 seek 는 메모리의 표본으로 처리한다.
  }, [url, overview, enabled, key]);
  return loaded?.key === key ? loaded.state : { status: "loading" };
}
